import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown } from 'lucide-react';
import { cn } from '../utils/helpers';

const ROOMS_SUBMENU = [
  { key: 'deluxe-double', path: '/rooms/deluxe-double' },
  { key: 'deluxe-twin', path: '/rooms/deluxe-twin' },
  { key: 'bungalow-deluxe', path: '/rooms/bungalow-deluxe' },
  { key: 'premier-deluxe', path: '/rooms/premier-deluxe' },
  { key: 'Family-bungalow-A', path: '/rooms/Family-bungalow-A' },
  { key: 'Family-bungalow-B', path: '/rooms/Family-bungalow-B' },
];

const FACILITIES_SUBMENU = [
  { label: { id: 'Semua Fasilitas', en: 'All Facilities', zh: '所有设施' }, path: '/facilities' },
  { label: { id: 'Kolam Renang', en: 'Outdoor Swimming Pool', zh: '室外游泳池' }, path: '/facilities/kolam-renang' },
  { label: { id: 'Taman & Lounge', en: 'Garden & Lounge', zh: '花园与休息室' }, path: '/facilities/taman-lounge' },
  { label: { id: 'Parkir Gratis', en: 'Free Safe Parking', zh: '免费安全停车' }, path: '/facilities/parkir' },
  { label: { id: 'Resepsionis 24 Jam', en: '24-Hour Front Desk', zh: '24小时前台' }, path: '/facilities/resepsionis' },
  { label: { id: 'Laundry', en: 'Laundry Service', zh: '洗衣服务' }, path: '/facilities/laundry' },
];

// Submenu baru untuk "Tentang"
const ABOUT_SUBMENU = [
  { label: { id: 'Tentang Kami', en: 'About Us', zh: '关于我们' }, path: '/about' },
  { label: { id: 'Galeri', en: 'Gallery', zh: '图库' }, path: '/gallery' },
];

const LANGUAGES = [
  { code: 'id', label: 'ID', flagUrl: 'https://flagcdn.com/w40/id.png' },
  { code: 'en', label: 'EN', flagUrl: 'https://flagcdn.com/w40/gb.png' },
  { code: 'zh', label: '中', flagUrl: 'https://flagcdn.com/w40/cn.png' },
];

const getRoomName = (key, lang) => {
  const names = {
    'deluxe-double': { id: 'Deluxe Double Room', en: 'Deluxe Double Room', zh: '豪华双人房' },
    'deluxe-twin': { id: 'Deluxe Twin Room', en: 'Deluxe Twin Room', zh: '豪华双床房' },
    'bungalow-deluxe': { id: 'Bungalow Deluxe', en: 'Bungalow Deluxe', zh: '豪华平房' },
    'premier-deluxe': { id: 'Premier Deluxe', en: 'Premier Deluxe', zh: '高级豪华房' },
    'Family-bungalow-A': { id: 'Family Bungalow A', en: 'Family Bungalow A', zh: '家庭平房 A' },
    'Family-bungalow-B': { id: 'Family Bungalow B', en: 'Family Bungalow B', zh: '家庭平房 B' },
  };
  return names[key][lang] || names[key]['en'];
};

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navRef = useRef(null);

  const lang = i18n.language || 'id';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Otomatis menutup menu mobile jika URL / halaman berpindah
  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const changeLanguage = (newLang) => {
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const isHome = location.pathname === '/';
  const navBg = scrolled || !isHome
    ? 'bg-dark/95 backdrop-blur-md shadow-lg'
    : 'bg-transparent';

  const getMenuTranslation = (key, defaultId, defaultEn, defaultZh) => {
    const translation = t(`nav.${key}`, { defaultValue: '' });
    if (translation) return translation;
    if (lang === 'zh') return defaultZh;
    return lang === 'id' ? defaultId : defaultEn;
  };

  return (
    <>
      {/* --- MAIN NAVBAR --- */}
      <nav
        ref={navRef}
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          navBg
        )}
      >
        <div className="hidden lg:flex items-center justify-between px-8 py-1.5 border-b border-white/10 text-xs text-white/60">
          <span className="font-sans tracking-widest uppercase font-semibold text-white/80">
          </span>
          <div className="flex items-center gap-6">
            <span>+62 813-7857-5758</span>
            <span>ndalem.mataram1@gmail.com</span>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 lg:px-8 py-4">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-amber-700/50 group-hover:border-amber-500 transition-colors flex-shrink-0 bg-white flex items-center justify-center">
              <img
                src="/images/logo-ndalem.jpg"
                alt="Ndalem Mataram"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<span style="font-weight:900;font-size:13px;color:#92400e;line-height:1">NM</span>';
                }}
              />
            </div>
            <div className="leading-tight">
              <div className="text-white font-display text-lg font-bold tracking-wide group-hover:text-amber-300 transition-colors">
                Ndalem Mataram
              </div>
              <div className="text-amber-500/80 text-[10px] font-sans tracking-[0.2em] uppercase">
                Banyuwangi
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <ul className="hidden lg:flex items-center gap-1 text-sm">
            <NavDropdown label={t('nav.rooms', { defaultValue: lang === 'id' ? 'Kamar Kami' : lang === 'zh' ? '我们的客房' : 'Our Rooms' })} isActive={activeDropdown === 'rooms'} onToggle={() => setActiveDropdown(activeDropdown === 'rooms' ? null : 'rooms')}>
              <Link to="/rooms" className="dropdown-item font-semibold text-accent-400 border-b border-white/10 pb-2 mb-1 block">
                {lang === 'id' ? 'Semua Kamar' : lang === 'zh' ? '所有客房' : 'All Rooms'}
              </Link>
              {ROOMS_SUBMENU.map((item) => (
                <Link key={item.key} to={item.path} className="dropdown-item block py-1.5 hover:text-accent-400">{getRoomName(item.key, lang)}</Link>
              ))}
            </NavDropdown>

            {/* Link langsung ke Pengalaman */}
            <NavLink to="/attractions">
              {getMenuTranslation('experiences', 'Pengalaman', 'Experiences', '体验')}
            </NavLink>

            <NavDropdown label={getMenuTranslation('facilities_menu', 'Fasilitas', 'Facilities', '设施')} isActive={activeDropdown === 'facilities'} onToggle={() => setActiveDropdown(activeDropdown === 'facilities' ? null : 'facilities')}>
              {FACILITIES_SUBMENU.map((item) => (
                <Link key={item.path} to={item.path} className="dropdown-item block py-1.5 hover:text-accent-400">{item.label[lang] || item.label.en}</Link>
              ))}
            </NavDropdown>

            {/* Dropdown Tentang (Tentang Kami & Galeri) */}
            <NavDropdown label={t('nav.about', { defaultValue: lang === 'id' ? 'Tentang' : lang === 'zh' ? '关于' : 'About' })} isActive={activeDropdown === 'about'} onToggle={() => setActiveDropdown(activeDropdown === 'about' ? null : 'about')}>
              {ABOUT_SUBMENU.map((item) => (
                <Link key={item.path} to={item.path} className="dropdown-item block py-1.5 hover:text-accent-400">{item.label[lang] || item.label.en}</Link>
              ))}
            </NavDropdown>

            <NavLink to="/contact">{t('nav.contact', { defaultValue: lang === 'id' ? 'Kontak' : lang === 'zh' ? '联系' : 'Contact' })}</NavLink>
          </ul>

          {/* Right actions Desktop */}
          <div className="flex items-center gap-4">
            <div className="hidden lg:flex items-center bg-white rounded-full p-1 border border-gray-200 shadow-sm">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => changeLanguage(l.code)}
                  className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all", lang === l.code ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800")}
                >
                  <img src={l.flagUrl} alt={l.label} className="w-4.5 h-3 object-cover rounded-sm border border-black/5 flex-shrink-0" />
                  <span className="tracking-wide">{l.label}</span>
                </button>
              ))}
            </div>

            <Link to="/rooms" className="hidden lg:flex items-center px-5 py-2 bg-amber-800 hover:bg-amber-700 text-white text-sm font-sans font-medium tracking-wide rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-amber-900/30">
              {t('nav.booking', { defaultValue: lang === 'id' ? 'Pesan Sekarang' : lang === 'zh' ? '立即预订' : 'Book Now' })}
            </Link>

            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-white p-2" aria-label="Open menu">
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* --- FULL-SCREEN MOBILE MENU --- */}
      <div
        className={cn(
          'fixed inset-0 z-[100] bg-white flex flex-col transition-all duration-300 lg:hidden',
          mobileOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-8'
        )}
      >
        {/* Header Mobile Menu */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 flex-shrink-0 bg-white flex items-center justify-center">
              <img src="/images/logo-ndalem.jpg" alt="Ndalem Mataram" className="w-full h-full object-contain" />
            </div>
          </Link>

          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="flex items-center bg-white rounded-full p-1 border border-gray-200 shadow-sm">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => changeLanguage(l.code)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all",
                    lang === l.code ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"
                  )}
                >
                  <img src={l.flagUrl} alt={l.label} className="w-4.5 h-3 object-cover rounded-sm border border-black/5" />
                  <span className="tracking-wide">{l.label}</span>
                </button>
              ))}
            </div>

            {/* Tombol X (Close) */}
            <button
              onClick={() => setMobileOpen(false)}
              className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-slate-600 hover:bg-gray-50 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body Mobile Menu (Daftar Link) */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <MobileNavDropdown label={t('nav.rooms', { defaultValue: lang === 'id' ? 'Kamar Kami' : lang === 'zh' ? '我们的客房' : 'Our Rooms' })} isActive={activeDropdown === 'mobile-rooms'} onToggle={() => setActiveDropdown(activeDropdown === 'mobile-rooms' ? null : 'mobile-rooms')}>
            <Link to="/rooms" className="block py-2 text-amber-600 font-semibold text-base">{lang === 'id' ? 'Semua Kamar' : lang === 'zh' ? '所有客房' : 'All Rooms'}</Link>
            {ROOMS_SUBMENU.map((item) => (
              <Link key={item.key} to={item.path} className="block py-2 text-slate-500 hover:text-slate-800 text-base">{getRoomName(item.key, lang)}</Link>
            ))}
          </MobileNavDropdown>

          <MobileNavDropdown label={getMenuTranslation('facilities_menu', 'Fasilitas', 'Facilities', '设施')} isActive={activeDropdown === 'mobile-facilities'} onToggle={() => setActiveDropdown(activeDropdown === 'mobile-facilities' ? null : 'mobile-facilities')}>
            {FACILITIES_SUBMENU.map((item) => (
              <Link key={item.path} to={item.path} className="block py-2 text-slate-500 hover:text-slate-800 text-base">{item.label[lang] || item.label.en}</Link>
            ))}
          </MobileNavDropdown>

          {/* Link langsung ke Pengalaman */}
          <MobileNavLink to="/attractions">
            {getMenuTranslation('experiences', 'Pengalaman', 'Experiences', '体验')}
          </MobileNavLink>

          {/* Dropdown Tentang (Tentang Kami & Galeri) */}
          <MobileNavDropdown label={t('nav.about', { defaultValue: lang === 'id' ? 'Tentang' : lang === 'zh' ? '关于' : 'About' })} isActive={activeDropdown === 'mobile-about'} onToggle={() => setActiveDropdown(activeDropdown === 'mobile-about' ? null : 'mobile-about')}>
            {ABOUT_SUBMENU.map((item) => (
              <Link key={item.path} to={item.path} className="block py-2 text-slate-500 hover:text-slate-800 text-base">{item.label[lang] || item.label.en}</Link>
            ))}
          </MobileNavDropdown>

          <MobileNavLink to="/contact">{t('nav.contact', { defaultValue: lang === 'id' ? 'Kontak' : lang === 'zh' ? '联系' : 'Contact' })}</MobileNavLink>
        </div>

        {/* Footer Mobile Menu (Tombol Booking) */}
        <div className="px-6 pb-8 pt-4 bg-white">
          <Link
            to="/rooms"
            className="block w-full py-4 bg-amber-800 hover:bg-amber-700 text-white text-center text-lg rounded-full font-sans font-semibold shadow-md transition-colors"
          >
            {t('nav.booking', { defaultValue: lang === 'id' ? 'Pesan Sekarang' : lang === 'zh' ? '立即预订' : 'Book Now' })}
          </Link>
        </div>
      </div>
    </>
  );
}

// --- KOMPONEN PEMBANTU ---
function NavLink({ to, children }) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <li>
      <Link
        to={to}
        className={cn(
          'px-3 py-2 rounded text-sm font-sans transition-colors block',
          isActive ? 'text-accent-400' : 'text-white/80 hover:text-white'
        )}
      >
        {children}
      </Link>
    </li>
  );
}

function NavDropdown({ label, children, isActive, onToggle }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {}
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <li className="relative" ref={dropdownRef}>
      <button
        onClick={onToggle}
        className="flex items-center gap-1 px-3 py-2 text-sm font-sans text-white/80 hover:text-white transition-colors"
      >
        {label}
        <ChevronDown size={13} className={cn('transition-transform', isActive && 'rotate-180')} />
      </button>
      <div
        className={cn(
          'absolute top-full left-0 mt-1 w-56 bg-dark/95 backdrop-blur-md border border-white/10 rounded-lg shadow-xl overflow-hidden transition-all duration-200 text-white/80',
          isActive ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
        )}
      >
        <div className="p-3 text-sm">{children}</div>
      </div>
    </li>
  );
}

function MobileNavLink({ to, children }) {
  return (
    <Link
      to={to}
      className="block text-slate-800 text-lg font-sans font-medium hover:text-amber-600 transition-colors"
    >
      {children}
    </Link>
  );
}

function MobileNavDropdown({ label, children, isActive, onToggle }) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-slate-800 text-lg font-sans font-medium hover:text-amber-600 transition-colors text-left"
      >
        {label}
        <ChevronDown size={20} className={cn('transition-transform text-slate-400', isActive && 'rotate-180')} />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300',
          isActive ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'
        )}
      >
        <div className="pl-4 border-l-2 border-gray-100 space-y-3">
          {children}
        </div>
      </div>
    </div>
  );
}